import { Injectable, inject } from '@angular/core';
import { AbilityService } from 'ability';
import { RoutingPath } from 'mbs-ui-shared@constants';
import { ConfigurationService } from 'mbs-ui-shared@services/configuration.service';

const baseHelpUrl = 'https://help.mspbackups.com';
const manageComputers = '/remote-management/manage-computers';
const backupHistory = '/reporting/monitoring-history/backup-history';
const haloPSA = '/integrations/halopsa/manage-integration';

const helpDictionaryRaw: Partial<Record<RoutingPath, string>> = {
  [RoutingPath.ApAdministrators]: '/organization/administrators/manage-administrators',
  [RoutingPath.ApAppsDomains]: '/gapps-office365/manage-domains',
  [RoutingPath.ApAppsGeneral]: '', // Not available yet
  [RoutingPath.ApAuditLog]: '/organization/audit-log',
  [RoutingPath.ApBackupComputers]: manageComputers,
  [RoutingPath.ApBackupHistory]: backupHistory,
  [RoutingPath.ApNewBackupHistoryDetails]: '/reporting/backup-history',
  [RoutingPath.ApBackupHistoryLegacy]: backupHistory,
  [RoutingPath.ApBackupTemplates]: '/remote-management/remote-deploy/manage-configurations',
  [RoutingPath.ApBackupTemplatesHistory]: '/remote-management/remote-deploy/backup-template-history',
  [RoutingPath.ApBranding]: '/organization/settings/branding',
  [RoutingPath.ApCompanies]: '/organization/companies',
  [RoutingPath.ApComputers]: manageComputers,
  [RoutingPath.ApConnectWiseNew]: '/integrations/connectwise-manage/scenario',
  [RoutingPath.ApDashboard]: '', // Should I implement this link?
  [RoutingPath.ApEmailService]: '/organization/settings/email-service',
  [RoutingPath.ApGlobalAgentOptions]: '/organization/settings/global-agent-options',
  [RoutingPath.ApHaloPSA]: haloPSA,
  [RoutingPath.ApLicenses]: '/licenses/manage-licenses',
  [RoutingPath.ApNewBackupHistory]: backupHistory,
  [RoutingPath.ApNewMonitoring]: '/reporting/monitoring-history/monitoring',
  [RoutingPath.ApNotifications]: '/organization/settings/notifications',
  [RoutingPath.ApPlanReportSettings]: '/reporting/plan-settings',
  [RoutingPath.ApPurchaseHistory]: '/organization/purchase-history',
  [RoutingPath.ApRDStatistics]: '/remote-management/connect/using-managed-remote-desktop/view-statistics',
  [RoutingPath.ApRestoreToCloud]: '/restore/restore2cloud/restore2cloud-intro',
  [RoutingPath.ApRMMAlertsHistory]: '/remote-management/rmm-service/rmm-alert-history',
  [RoutingPath.ApRMMComputers]: manageComputers,
  [RoutingPath.ApRMMGroupTaskAction]: '/remote-management/rmm-service/management/group-actions-new',
  [RoutingPath.ApScheduledReports]: '/reporting/reports',
  [RoutingPath.ApSettings]: '/organization/settings/general-settings',
  [RoutingPath.ApSnmp]: '/remote-management/rmm-service/management/snmp',
  [RoutingPath.ApSSL]: '/security/bindings-certificates',
  [RoutingPath.ApStorageCapacityReport]: '/billing-storage/storage-usage',
  [RoutingPath.ApStorages]: '/billing-storage/storage-accounts',
  [RoutingPath.ApBackupStorage]: '/billing-storage/storage-accounts',
  [RoutingPath.ApStorageUsage]: '/billing-storage/storage-usage',
  [RoutingPath.ApUsers]: '/organization/users/create-users',
  [RoutingPath.EmailsCustomization]: '/reporting/email-templates',
  [RoutingPath.NotificationErrors]: '/organization/settings/email-service',
  [RoutingPath.RMMAPITokens]: '/m0793',
  [RoutingPath.RMMNotifications]: '/remote-management/rmm-service/manage-alerts',
  [RoutingPath.RMMScriptLibrary]: '/remote-management/rmm-service/management/script-library',
  [RoutingPath.StorageLimits]: '/billing-storage/storage-limits/manage-limit',
  [RoutingPath.WebConsoleRebranding]: '/rebranding/rebrand-console',
  [RoutingPath.ApTickets]: '/remote-management/manage-tickets',
  [RoutingPath.ApTicketSettings]: '/organization/settings/built-in-tickets'
};

export enum HelpLinkAspect {
  'postPayment' = 'postPayment',
  'Authentication' = 'Authentication',
  'Company Mapping' = 'CompanyMapping',
  'Computer Mapping' = 'ComputerMapping',
  'Product Mapping' = 'ProductMapping',
  'Ticket Settings' = 'TicketSettings',
  'Backup Tickets' = 'BackupTickets',
  'RMM Tickets' = 'RmmTickets',
  'RMM API tokens' = 'RmmAPITokens',
  'Billing' = 'Billing'
}

const helpDictionary: Record<string, string> = {
  ...helpDictionaryRaw,
  [`${RoutingPath.ApLicenses}-${HelpLinkAspect.postPayment}`]: '/organization/licenses/modes/post-payment',
  [`${RoutingPath.ApHaloPSA}-${HelpLinkAspect.Authentication}`]: '/integrations/halopsa/manage-integration',
  [`${RoutingPath.ApHaloPSA}-${HelpLinkAspect['Company Mapping']}`]: '/integrations/halopsa/map-companies',
  [`${RoutingPath.ApHaloPSA}-${HelpLinkAspect['Computer Mapping']}`]: '/integrations/halopsa/map-computers',
  [`${RoutingPath.ApHaloPSA}-${HelpLinkAspect['Product Mapping']}`]: '/integrations/halopsa/map-products',
  [`${RoutingPath.ApHaloPSA}-${HelpLinkAspect['Ticket Settings']}`]: haloPSA,
  [`${RoutingPath.ApHaloPSA}-${HelpLinkAspect['Backup Tickets']}`]: haloPSA,
  [`${RoutingPath.ApHaloPSA}-${HelpLinkAspect['RMM Tickets']}`]: haloPSA,
  [`${RoutingPath.ApHaloPSA}-${HelpLinkAspect['Billing']}`]: haloPSA
};

@Injectable({
  providedIn: 'root'
})
export class HelpService {
  private config = inject(ConfigurationService);
  private ability = inject(AbilityService);

  public getHelpLink(pageAddress: string): string {
    return (this.config.get('helpUrl') || baseHelpUrl) + (pageAddress || '');
  }

  public openHelpPage(path: string, aspect?: HelpLinkAspect): void {
    const link = aspect ? helpDictionary[`${path}-${aspect}`] : helpDictionary[path];

    this.openWindow(this.getHelpLink(link || ''), '_blank');
  }

  public hasRights(): boolean {
    return this.ability.can('read', 'HelpMarketing');
  }

  private openWindow(url: string, target?: string): void {
    window.open(url, target);
  }
}
